const TAG_NULL = 0;
const TAG_FALSE = 1;
const TAG_TRUE = 2;
const TAG_UINT = 3;
const TAG_NINT = 4;
const TAG_FLOAT = 5;
const TAG_STR = 6;
const TAG_ARR = 7;
const TAG_OBJ = 8;
const TAG_BIN = 9;

const MAX_UINT32 = 0xffffffff;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

class Writer {
	buf: Uint8Array;

	view: DataView;

	offset = 0;

	constructor(size = 256) {
		this.buf = new Uint8Array(size);
		this.view = new DataView(this.buf.buffer);
	}

	ensure(extra: number) {
		const need = this.offset + extra;
		if (need <= this.buf.length) {
			return;
		}
		let next = this.buf.length * 2;
		while (next < need) {
			next *= 2;
		}
		const grown = new Uint8Array(next);
		grown.set(this.buf.subarray(0, this.offset));
		this.buf = grown;
		this.view = new DataView(grown.buffer);
	}

	byte(value: number) {
		this.ensure(1);
		this.buf[this.offset] = value;
		this.offset += 1;
	}

	uvarint(value: number) {
		let n = value >>> 0;
		this.ensure(5);
		while (n >= 0x80) {
			this.buf[this.offset] = (n & 0x7f) | 0x80;
			this.offset += 1;
			n >>>= 7;
		}
		this.buf[this.offset] = n;
		this.offset += 1;
	}

	bytes(data: Uint8Array) {
		this.ensure(data.byteLength);
		this.buf.set(data, this.offset);
		this.offset += data.byteLength;
	}

	utf8(text: string) {
		const encoded = textEncoder.encode(text);
		this.uvarint(encoded.length);
		this.bytes(encoded);
	}

	float64(value: number) {
		this.ensure(8);
		this.view.setFloat64(this.offset, value, true);
		this.offset += 8;
	}

	finish(): Uint8Array {
		return this.buf.subarray(0, this.offset);
	}
}

class Reader {
	buf: Uint8Array;

	view: DataView;

	offset: number;

	end: number;

	constructor(buf: Uint8Array, offset = 0, end = buf.length) {
		this.buf = buf;
		this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
		this.offset = offset;
		this.end = end;
	}

	byte(): number {
		if (this.offset >= this.end) {
			throw new Error("compact: unexpected end");
		}
		const value = this.buf[this.offset];
		this.offset += 1;
		return value;
	}

	uvarint(): number {
		let result = 0;
		let shift = 0;
		while (shift <= 28) {
			const b = this.byte();
			result += (b & 0x7f) * 2 ** shift;
			if ((b & 0x80) === 0) {
				return result;
			}
			shift += 7;
		}
		throw new Error("compact: varint overflow");
	}

	take(size: number): Uint8Array {
		if (size < 0 || this.offset + size > this.end) {
			throw new Error("compact: unexpected end");
		}
		const slice = this.buf.subarray(this.offset, this.offset + size);
		this.offset += size;
		return slice;
	}

	float64(): number {
		if (this.offset + 8 > this.end) {
			throw new Error("compact: unexpected end");
		}
		const value = this.view.getFloat64(this.offset, true);
		this.offset += 8;
		return value;
	}
}

function writeNumber(writer: Writer, value: number) {
	if (!Number.isFinite(value)) {
		writer.byte(TAG_NULL);
		return;
	}
	if (Number.isInteger(value) && value >= 0 && value <= MAX_UINT32) {
		writer.byte(TAG_UINT);
		writer.uvarint(value);
		return;
	}
	if (Number.isInteger(value) && value < 0 && value >= -MAX_UINT32) {
		writer.byte(TAG_NINT);
		writer.uvarint(-value);
		return;
	}
	writer.byte(TAG_FLOAT);
	writer.float64(value);
}

function writeBinary(writer: Writer, data: Uint8Array) {
	writer.byte(TAG_BIN);
	writer.uvarint(data.length);
	writer.bytes(data);
}

function writeString(writer: Writer, value: string) {
	writer.byte(TAG_STR);
	writer.utf8(value);
}

function isSkippedObjectValue(item: unknown): boolean {
	if (item === undefined) {
		return true;
	}
	if (typeof item === "function") {
		return true;
	}
	return typeof item === "symbol";
}

function writeArray(writer: Writer, value: unknown[], seen: Set<object>) {
	writer.byte(TAG_ARR);
	writer.uvarint(value.length);
	for (let i = 0; i < value.length; i += 1) {
		const item = value[i];
		if (item === undefined) {
			writeValue(writer, null, seen);
		} else {
			writeValue(writer, item, seen);
		}
	}
}

function writeObject(
	writer: Writer,
	value: Record<string, unknown>,
	seen: Set<object>,
) {
	const keys = Object.keys(value);
	const kept: string[] = [];
	for (let i = 0; i < keys.length; i += 1) {
		if (!isSkippedObjectValue(value[keys[i]])) {
			kept.push(keys[i]);
		}
	}

	writer.byte(TAG_OBJ);
	writer.uvarint(kept.length);
	for (let i = 0; i < kept.length; i += 1) {
		const key = kept[i];
		writer.utf8(key);
		writeValue(writer, value[key], seen);
	}
}

function writeValue(writer: Writer, value: unknown, seen: Set<object>) {
	if (value === null || value === undefined) {
		writer.byte(TAG_NULL);
		return;
	}
	if (value === false) {
		writer.byte(TAG_FALSE);
		return;
	}
	if (value === true) {
		writer.byte(TAG_TRUE);
		return;
	}
	if (typeof value === "number") {
		writeNumber(writer, value);
		return;
	}
	if (typeof value === "string") {
		writeString(writer, value);
		return;
	}
	if (typeof value === "bigint") {
		writeString(writer, value.toString());
		return;
	}
	if (value instanceof Uint8Array) {
		writeBinary(writer, value);
		return;
	}
	if (ArrayBuffer.isView(value)) {
		writeBinary(
			writer,
			new Uint8Array(value.buffer, value.byteOffset, value.byteLength),
		);
		return;
	}
	if (typeof value !== "object") {
		writer.byte(TAG_NULL);
		return;
	}

	const objectValue = value as Record<string, unknown> & {
		toJSON?: () => unknown;
	};
	if (typeof objectValue.toJSON === "function") {
		writeValue(writer, objectValue.toJSON(), seen);
		return;
	}

	if (seen.has(objectValue)) {
		throw new TypeError("compact: converting circular structure to bytes");
	}
	seen.add(objectValue);
	if (Array.isArray(value)) {
		writeArray(writer, value, seen);
	} else {
		writeObject(writer, objectValue, seen);
	}
	seen.delete(objectValue);
}

function readValue(reader: Reader): unknown {
	const tag = reader.byte();
	if (tag === TAG_NULL) {
		return null;
	}
	if (tag === TAG_FALSE) {
		return false;
	}
	if (tag === TAG_TRUE) {
		return true;
	}
	if (tag === TAG_UINT) {
		return reader.uvarint();
	}
	if (tag === TAG_NINT) {
		return -reader.uvarint();
	}
	if (tag === TAG_FLOAT) {
		return reader.float64();
	}
	if (tag === TAG_STR) {
		return textDecoder.decode(reader.take(reader.uvarint()));
	}
	if (tag === TAG_ARR) {
		const length = reader.uvarint();
		const list = new Array(length);
		for (let i = 0; i < length; i += 1) {
			list[i] = readValue(reader);
		}
		return list;
	}
	if (tag === TAG_OBJ) {
		const count = reader.uvarint();
		const objectValue: Record<string, unknown> = {};
		for (let i = 0; i < count; i += 1) {
			const key = textDecoder.decode(reader.take(reader.uvarint()));
			objectValue[key] = readValue(reader);
		}
		return objectValue;
	}
	if (tag === TAG_BIN) {
		return reader.take(reader.uvarint());
	}
	throw new Error(`compact: unknown tag ${tag}`);
}

export function isCompactPayload(buf: Uint8Array): boolean {
	if (buf.length === 0) {
		return false;
	}
	return buf[0] <= TAG_BIN;
}

export function encodeCompact(value: unknown): Uint8Array {
	const writer = new Writer();
	writeValue(writer, value, new Set());
	return writer.finish();
}

export function decodeCompact(buf: Uint8Array): object {
	const reader = new Reader(buf);
	return readValue(reader) as object;
}
